import { useState, useMemo } from "react";
import { LeafletStaticMap } from "../../../../components/Leaflet/LeafletStaticMap/Index";
import { FlyToBounds } from "../../../../components/Leaflet/FlyToBounds/Index";
import { Polygon } from "react-leaflet";
import { ICoords } from "../../../../@types/GoogleMaps/ICoord";
import { IFarmOnboarding } from "../../../../@types/Onboarding/IFarm";
import { IAreas } from "../../../../@types/app/IArea";
import { IMicroAreas } from "../../../../@types/app/IMicroArea";
import Constants from "../../../../constants/Index";
import "./FarmMapCard.css";

type ViewMode = "farm" | "areas" | "microareas";

interface FarmMapCardProps {
  farm: IFarmOnboarding;
  areas: IAreas;
  microAreas: IMicroAreas;
}

export function FarmMapCard({ farm, areas, microAreas }: FarmMapCardProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("farm");

  // Filtrar retiros da fazenda
  const farmAreas = useMemo(() => {
    return areas.filter(area => area.farm_id === farm.uuid);
  }, [areas, farm.uuid]);

  // Filtrar pastos dos retiros da fazenda
  const farmMicroAreas = useMemo(() => {
    const areaIds = farmAreas.map(area => area.uuid);
    return microAreas.filter(microArea =>
      areaIds.includes(microArea.area_id)
    );
  }, [microAreas, farmAreas]);

  // Obter coordenadas para exibir no mapa baseado no modo de visualização
  const getCoordsToDisplay = (): ICoords[] => {
    switch (viewMode) {
      case "farm":
        return farm.map_coords ? [farm.map_coords] : [];
      case "areas":
        return farmAreas.map(area => area.map_coords?.coords || []).filter(coords => coords.length > 0);
      case "microareas":
        return farmMicroAreas.map(microArea => microArea.map_coords?.coords || []).filter(coords => coords.length > 0);
      default:
        return [];
    }
  };

  // Sempre usar as coordenadas da fazenda para o zoom do mapa
  const farmCoords = farm.map_coords || [];

  // Obter o centro da fazenda para inicializar o mapa
  const farmCenter = farmCoords.length > 0 ? farmCoords[0] : undefined;

  const coordsToDisplay = getCoordsToDisplay();

  // Definir cores para cada tipo de polígono
  const getPolygonColor = (mode: ViewMode) => {
    switch (mode) {
      case "farm":
        return Constants.LEAFLET_NORMAL_BLUE;
      case "areas":
        return Constants.LEAFLET_NORMAL_BLUE;
      case "microareas":
        return Constants.LEAFLET_NORMAL_BLUE;
      default:
        return Constants.LEAFLET_NORMAL_BLUE;
    }
  };

  return (
    <div className="farm-map-card">
      {/* Cabeçalho */}
      <div className="farm-map-card__header">
        <h3>{farm.name} - {farm.initials}</h3>
      </div>

      {/* Botões de alternância */}
      <div className="farm-map-card__buttons">
        <button
          className={`farm-map-card__button ${viewMode === "farm" ? "farm-map-card__button--active" : ""}`}
          onClick={() => setViewMode("farm")}
        >
          Fazenda
        </button>
        <button
          className={`farm-map-card__button ${viewMode === "areas" ? "farm-map-card__button--active" : ""}`}
          onClick={() => setViewMode("areas")}
        >
          Retiros ({farmAreas.length})
        </button>
        <button
          className={`farm-map-card__button ${viewMode === "microareas" ? "farm-map-card__button--active" : ""}`}
          onClick={() => setViewMode("microareas")}
        >
          Pastos ({farmMicroAreas.length})
        </button>
      </div>

      {/* Mapa */}
      <div className="farm-map-card__map">
        <LeafletStaticMap center={farmCenter}>
          <FlyToBounds
            fitCoords={farmCoords}
          >
            {coordsToDisplay.map((coords, index) => (
              <Polygon
                key={`${viewMode}-${index}`}
                positions={coords}
                pathOptions={getPolygonColor(viewMode)}
              />
            ))}
          </FlyToBounds>
        </LeafletStaticMap>
      </div>
    </div>
  );
}
